import styles from "../styles/Details.module.scss";
import CloseTrailer from "../../../components/icons/details/CloseThrailer";
import { useGetCurrentVideoQuery } from "../../../store/api/filmAPI";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import MobileCover from "../../foryou/_components/MobileComponents/MobileCover";
import PlayerControllerWrapper from "../../foryou/_components/playerController/PlayerControllerWrapper";
import MobileProgressController from "../../foryou/_components/MobileComponents/MobileProgressController";
import useWindowSize from "../../../hooks/useWindowSize";
import { isIosSafari, languageHandler } from "../../../utils/helpers";
import MutedButtonOn from "../../../components/icons/MutedButtonOn";
import MutedButtonOff from "../../../components/icons/MutedButtonOff";

interface IProps {
  closeHandler: () => void;
  item: any;
  externalUrl: string;
}

const TrailerModal = ({ closeHandler, item, externalUrl }: IProps) => {
  const [url, setUrl] = useState("");
  const [muted, setMuted] = useState(isIosSafari);
  const [player, setPlayer] = useState(null);
  const [time, setTime] = useState<number | number[]>(0);
  const [maxValue, setMaxValue] = useState<number | number[]>(0);
  const [ready, setReady] = useState(false);
  const [displayVolume, setDisplayVolume] = useState<boolean>(false);
  const [playing, setPlaying] = useState(true);


  const { windowSize } = useWindowSize();
  const isMobile = windowSize === "mobile";
  const { i18n, t } = useTranslation();

  const { data } = useGetCurrentVideoQuery(
    {
      queries: { language: languageHandler(i18n.language) },
      id: item.id,
    },
    {
      skip: externalUrl !== "",
    },
  );

  useEffect(() => {
    if (externalUrl) {
      setUrl(externalUrl);
      return;
    }

    const trailer = data?.results.find((item: any) => item.type === "Trailer") || data?.results[0];
    setUrl(trailer ? trailer?.key : "");
  }, [data, externalUrl]);

  useEffect(() => {
    const element = document.getElementById("mobileCover");
    if (!element || isMobile) return;

    let timerId: number | any = null;

    const handleMouseMove = (e: any) => {
      if (!displayVolume) setDisplayVolume(true);

      clearTimeout(timerId);
      timerId = setTimeout(() => {
        setDisplayVolume(false);
      }, 3000);
    };

    const handleMouseLeave = (event: any) => {
      if (element.contains(event.relatedTarget)) {
        return;
      }
      setDisplayVolume(false);
    };

    element.addEventListener("mousemove", handleMouseMove);
    element.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      element.removeEventListener("mousemove", handleMouseMove);
      element.removeEventListener("mouseleave", handleMouseLeave);
      clearTimeout(timerId);
    };
  }, [displayVolume, isMobile]);

  const toggleMuted = () => {
    const nextMuted = !muted;
    const youtubePlayer = player as any;

    setMuted(nextMuted);

    if (nextMuted) {
      youtubePlayer?.mute?.();
    } else {
      youtubePlayer?.unMute?.();
      youtubePlayer?.setVolume?.(100);
    }
  };

  return (
    <div className={styles.trailerModalWrapper}>
      <div className={styles.relWrapper}>
        <div className={styles.topCover} />
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            closeHandler();
          }}
          className={styles.closeTrailerBtn}
          aria-label="Close trailer"
        >
          <CloseTrailer />
        </button>
        {!isMobile && (
          <div id={"mobileCover"} className={styles.coversWrapper}>
            <MobileCover ready={ready} />
            <div className={styles.trailerDesktopControls}>
              <PlayerControllerWrapper
                time={time}
                maxValue={maxValue}
                player={player}
                setTime={setTime}
                displayVolume={true}
                muted={muted}
                onToggleMuted={toggleMuted}
              />
            </div>
          </div>
        )}
        {isMobile && !ready && (
          <div className={styles.mobileLoadingCover}>
            <MobileCover ready={ready} />
          </div>
        )}
        <div style={{ width: "100%", height: "100vh" }} className={styles.plWrapper}>
          {url && (
            <ReactPlayer
              className={isMobile ? styles.trailerMobilePlayerFrame : undefined}
              controls={false}
              width={"100%"}
              height={"100%"}
              playsinline={true}
              progressInterval={500}
              onProgress={(value: any) => setTime(Math.round(value.playedSeconds))}
              playing={playing}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              loop={true}
              muted={muted}
              fullscreen={!isMobile}
              url={`https://www.youtube.com/watch?v=${url}`}
              onReady={(e: any) => {
                setPlayer(e["player"]);
                const duration = e["player"].getDuration();
                setMaxValue(duration);

                setTimeout(() => {
                  setReady(true);
                }, 200);
              }}
            />
          )}
        </div>
        {isMobile && url && (
          <button
            type="button"
            className={styles.trailerMobileVideoToggle}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              const youtubePlayer = player as any;
              if (playing) {
                youtubePlayer?.pauseVideo?.();
                setPlaying(false);
              } else {
                youtubePlayer?.playVideo?.();
                setPlaying(true);
              }
            }}
            aria-label={playing ? "Pause video" : "Play video"}
          />
        )}
        {isMobile && url && (
          <button
            type="button"
            className={`${styles.trailerMobileMuteButton} ${muted ? styles.muted : styles.unmuted}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              toggleMuted();
            }}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            <span key={muted ? "muted" : "unmuted"} className={styles.trailerMobileMuteIcon}>
              {muted ? <MutedButtonOn /> : <MutedButtonOff />}
            </span>
            <span>
              {muted ? (t("turn_on")) : (t("turn_off"))}
            </span>
          </button>
        )}
        <div className={styles.bottomCover} />
        {isMobile && (
          <div className={styles.trailerMobileProgressLayer}>
            <MobileProgressController
              maxValue={maxValue as number}
              time={time as number}
              player={player as any}
              setTime={setTime}
              isMobile={isMobile}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TrailerModal;
